import React, { useState } from 'react';
import { theme } from '../theme';
import { BentoCard, ReviewChip } from './shared';
import { pillFace } from './SecondaryAxisPills';
import {
  listReplyPrompts, saveReplyPrompt, rollbackReplyPrompt, resetReplyPrompt, ReplyPromptEntry,
} from '../services/api';
import { formatBackendError } from '../utils/formatError';
import { useRevisionedDrafts } from '../hooks/useRevisionedDrafts';
import { btnStyle, feedbackStyle, overlayChipStyle, InlineErrorZone, staleConflictNotice } from './overlayEditorChrome';

// <purpose>The reply-prose SECTION of the "Texte & Vokabular" tab (was its own Antwort-Texte tab
// until the tab generalized). Read-write mirror of the reply-engine's
// overlay-backed prompt components (persona role/tone/length, letter Anrede/Grußformel, Eingangs-
// bestätigungen, Weiterverweise, Rückfragen, Holding-Antworten, Notfall) — DB-gestützt, kein Deploy.
// Successor to the pre-overlay ReplyPromptsVerwaltung draft (origin/Sven_TestBranch): that component
// wrote straight through (upsertReplyPrompt); this one is overlay-aware — every write is optimistic
// (baseRevision guards a lost-update 409), reversible (rollback), and resettable (drop the override,
// the git-seeded text shows through again).</purpose>
// <dependencies>GET/POST /manage/reply-prompts, POST /manage/reply-prompts/{c}/rollback, DELETE
// /manage/reply-prompts/{c} (services/api.ts); BentoCard/ReviewChip (shared.tsx); formatBackendError
// (utils/formatError — 400 validation messages pass through verbatim from the BE's ErrorDe);
// onNavigateToFirmendaten (optional — EinstellungenSeite wires it to its own setActiveTab('firma'),
// same in-SPA tab-routing every other Firmendaten deep-link reuses; omitted in tests/callers that
// don't need it, in which case the frozen card's Firmendaten link simply doesn't render — a link
// with nothing to link to is worse than no link).</dependencies>
// <constraints>Admin-gated by the caller (EinstellungenSeite renders this only for canManageUsers).
// Frozen components (ReplyPromptEntry.frozen — absent on older BE builds MUST read as false, never
// assume frozen) render INLINE in the SAME list, SAME order, SAME card structure as every editable
// entry — no separate section (a live review rejected an earlier "Geprüfte Texte" trailing section
// as a bolted-on appendage). Only these things differ per-card for a frozen entry: the badge-row
// pill reads "nur Ansicht" instead of the editable pill (see next paragraph), the textarea is
// `disabled` at `opacity: 0.6` (the same disabled-input convention AntwortKatalog's act-id field
// uses), no Save/Rollback/Reset buttons render — instead the action row carries a single
// "Firmendaten" link (round 2 had this plus an explanatory sentence; a live review found the
// sentence redundant and cut it, but the operator wants the bare link back — it is the ONLY thing
// in the action row for a frozen card), and no caution badge (that badge signals "editable, tread
// carefully" — moot once editing is impossible). The reserved error zone still renders
// unconditionally for every entry; a frozen one simply never populates it, having no
// save/reset/rollback path to fail.
// Badge-row pill: renders ONLY when it conveys something — "nur Ansicht" for frozen, or
// "Angepasst · Rev N" for a non-frozen override. A bare "Standard" pill for the non-override,
// non-frozen case was found meaningless (every card defaults to Standard; the pill added no
// information) and was removed — this is the ONE intentional behavioral delta from `origin/main`'s
// pre-branch badge row beyond the frozen-card additions themselves.
// Per-card save/reset/rollback failures surface INLINE below that card's action row, in a
// fixed-height reserved zone (never absolutely positioned over the gap — a wrapping 2-line message
// doesn't fit that gap and would spill into the next card) instead of only the top banner, which
// the operator found could sit out of the viewport; the top `error`/`notice` banner is kept for
// load failures and the 409 stale-conflict notice, which are page-level, not per-card.
// The reload-is-not-reseed merge rule and the frozen-baseRevision rule that make a 409 recoverable
// used to be spelled out here; they now live ONCE in useRevisionedDrafts' <constraints>, because
// the vocabulary section obeys exactly the same rules and a second copy would have been a
// second thing to keep true. Read them there. What remains specific to THIS section: rollback only
// ever steps back ONE revision (activeRevision-1) — a full revision-history browser/picker is a
// deliberate follow-up, not scoped here.</constraints>

const inputStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, border: `1px solid ${theme.colors.bgLight}`,
  fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box',
  minHeight: 90, resize: 'vertical',
};

// A frozen card's ONLY action-row content — no chrome (background/border none, font: inherit,
// same no-chrome-button idiom PanelSection's collapsible header uses in this file's own component
// tree) + underline for link affordance. Just the link, deliberately no surrounding sentence (see
// <constraints>).
function firmendatenLinkStyle(): React.CSSProperties {
  return {
    background: 'none', border: 'none', padding: 0, margin: 0,
    font: 'inherit', fontSize: 13, color: theme.colors.navy, textDecoration: 'underline',
    cursor: 'pointer',
  };
}

// ── One group per pipeline (part 5-2, 2026-10-02) ──
// The catalog serves rows from every editable overlay pipeline, each DTO naming its own. The rows
// group under one small heading per pipeline, in the order the groups first appear in the served
// list (core orders the catalog; this surface never re-sorts it). A row with no `pipeline` comes
// from an older backend that served the reply templates alone, so it joins that group. An unknown
// pipeline id is headed by the id itself — visible, never dropped.
const DEFAULT_PIPELINE = 'vnb_template';
// FIRST DRAFTS (German review round): the group headings.
const PIPELINE_HEADINGS: Record<string, string> = {
  vnb_template: 'Antwortbausteine',
  assessment: 'Gefahr, Rechtsbezug und Angriff',
  brand: 'Chatbot',
};

const groupByPipeline = (entries: readonly ReplyPromptEntry[]) => {
  const groups: { pipeline: string; entries: ReplyPromptEntry[] }[] = [];
  for (const entry of entries) {
    const pipeline = entry.pipeline ?? DEFAULT_PIPELINE;
    const group = groups.find(g => g.pipeline === pipeline);
    if (group) group.entries.push(entry);
    else groups.push({ pipeline, entries: [entry] });
  }
  return groups;
};

const AntwortTexte: React.FC<{
  // EinstellungenSeite passes () => setActiveTab('firma') — the app has no cross-page router,
  // tab state is the existing "deep link" mechanism (see resolveInitialTab in EinstellungenSeite),
  // so this is that same mechanism reused, not a new navigation concept.
  onNavigateToFirmendaten?: () => void;
}> = ({ onNavigateToFirmendaten }) => {
  // The load-merge / frozen-baseRevision / feedback-slot machinery is the tab's SHARED engine
  // (useRevisionedDrafts) — the vocabulary section drives the same one. What stays local here is
  // only what is specific to prose: the per-action in-flight flags and the reset confirmation.
  const {
    rows: entries, drafts, loading, error, notice, fieldErrors,
    setNotice, load, setDraftValue, baseRevisionFor, clearFreeze, rebaseFreeze,
    setFieldError, clearFieldError, isDirty,
  } = useRevisionedDrafts<ReplyPromptEntry>(
    listReplyPrompts,
    entry => ({ key: entry.component, effectiveText: entry.effectiveText, latestRevision: entry.latestRevision }),
    'Laden der Antwort-Texte',
  );
  const [savingComponent, setSavingComponent] = useState('');
  const [savedComponent, setSavedComponent] = useState('');
  const [resetConfirmComponent, setResetConfirmComponent] = useState<string | null>(null);
  const [resettingComponent, setResettingComponent] = useState('');
  const [rollingBackComponent, setRollingBackComponent] = useState('');

  // Why reload-after-write rather than patch-in-place: the BE owns revision numbering (rollback/
  // reset make it non-linear), so the client cannot reliably compute the new activeRevision/source
  // itself — refetching is the only source of truth, for both the happy path and the 409 recovery.
  const save = async (entry: ReplyPromptEntry) => {
    setSavingComponent(entry.component); setNotice(''); setSavedComponent('');
    clearFieldError(entry.component);
    // Dirty drafts send their frozen snapshot, not the possibly-rebased entry.latestRevision —
    // see the frozen-baseRevision rule in useRevisionedDrafts' <constraints>.
    const baseRevision = baseRevisionFor(entry);
    try {
      await saveReplyPrompt(entry.component, drafts[entry.component] ?? '', baseRevision);
      clearFreeze(entry.component);
      setSavedComponent(entry.component);
      await load();
    } catch (e: any) {
      if (e?.response?.status === 409) {
        setNotice(staleConflictNotice('Der Text'));
        // The notice above makes this rebase user-acknowledged — it is the ONE place the freeze
        // is allowed to move forward without a fresh explicit edit.
        rebaseFreeze(await load(), entry.component);
      } else {
        // formatBackendError passes the BE's ErrorDe (400 validation, e.g. Notfall contract) through.
        // Inline at the card (not the top banner, which the operator found could sit out of the
        // viewport) — see InlineErrorZone + <constraints>.
        setFieldError(entry.component, formatBackendError(e, 'Speichern des Textes'));
      }
    } finally {
      setSavingComponent('');
    }
  };

  const resetToStandard = async (component: string) => {
    setResettingComponent(component); setNotice('');
    clearFieldError(component);
    try {
      await resetReplyPrompt(component);
      clearFreeze(component);
      setResetConfirmComponent(null);
      await load();
    } catch (e) {
      setFieldError(component, formatBackendError(e, 'Zurücksetzen auf Standard'));
    } finally {
      setResettingComponent('');
    }
  };

  const rollbackOne = async (entry: ReplyPromptEntry) => {
    if (entry.activeRevision == null || entry.activeRevision <= 1) return;
    setRollingBackComponent(entry.component); setNotice('');
    clearFieldError(entry.component);
    try {
      await rollbackReplyPrompt(entry.component, entry.activeRevision - 1);
      clearFreeze(entry.component);
      await load();
    } catch (e) {
      setFieldError(entry.component, formatBackendError(e, 'Vorherige Version aktivieren'));
    } finally {
      setRollingBackComponent('');
    }
  };

  return (
    <BentoCard title="Antwort-Texte">
      <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
        <p style={{ fontSize: 12, color: theme.colors.textSecondary, margin: 0 }}>
          Rolle, Ton und Länge der KI-Antwort sowie alle festen Bausteine (Anrede, Grußformel,
          Bestätigungen, Weiterverweise). Änderungen wirken beim nächsten Reanalyze — ohne Deployment.
        </p>

        {error && <div style={feedbackStyle(true)}>{error}</div>}
        {notice && <div style={feedbackStyle(false)}>{notice}</div>}
        {/* Pre-allocated slot, mirroring the vocabulary section: this hint already rendered BESIDE
            mounted rows (rows here were never gated on `loading`), but it mounted and unmounted in
            flow, so every save nudged the whole list down and back. The box now stays; only the
            text inside it changes. */}
        <div style={{ minHeight: 18 }}>
          {loading && <span style={{ color: theme.colors.textSecondary, fontSize: 13 }}>Lädt…</span>}
        </div>

        {/* Frozen (BE-locked, e.g. Notfall) entries render INLINE in this same loop, same order —
            no separate section (see <constraints>). Absent `frozen` reads as false (older BE
            builds), so an entry only gets the frozen treatment when the BE explicitly says so. */}
        {groupByPipeline(entries).map(group => (
          <section key={group.pipeline} data-testid={`reply-prompt-group-${group.pipeline}`}
            style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
            <h4 style={{ margin: 0, fontSize: 12, fontWeight: 600, color: theme.colors.textSecondary, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              {PIPELINE_HEADINGS[group.pipeline] ?? group.pipeline}
            </h4>
        {group.entries.map(entry => {
          const isFrozen = !!entry.frozen;
          const draft = drafts[entry.component] ?? '';
          const dirty = !isFrozen && isDirty(entry);
          const canRollback = !isFrozen && entry.source === 'override' && entry.activeRevision != null && entry.activeRevision > 1;

          return (
            <div key={entry.component} style={{ display: 'flex', flexDirection: 'column', gap: 6,
              borderTop: `1px solid ${theme.colors.bgLight}`, paddingTop: theme.spacing.md }}>
              <label htmlFor={`rp-${entry.component}`} style={{ fontSize: 13, fontWeight: 600, color: theme.colors.navyMid }}>
                {entry.label}
                <span style={{ fontWeight: 400, color: theme.colors.textSecondary, marginLeft: 8, fontFamily: 'monospace', fontSize: 11 }}>
                  {entry.component}
                </span>
              </label>

              {/* Pre-allocated badge row — always rendered so its presence/absence per entry never
                  shifts the label/textarea above or below it (Layout-Stability). The pill inside
                  only renders when it says something real: "nur Ansicht" for a frozen entry,
                  "Angepasst · Rev N" for a non-frozen override. A bare "Standard" pill conveyed
                  nothing and was removed (see <constraints>). */}
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', minHeight: 18 }}>
                {isFrozen ? (
                  <span style={overlayChipStyle('frozen')}>{pillFace('info', 'nur Ansicht')}</span>
                ) : entry.source === 'override' ? (
                  <span style={overlayChipStyle('override')}>{pillFace('info', `Angepasst · Rev ${entry.activeRevision ?? '?'}`)}</span>
                ) : null}
                {!isFrozen && entry.caution && <ReviewChip label="Nur mit Bedacht ändern" />}
              </div>

              <textarea
                id={`rp-${entry.component}`}
                style={{ ...inputStyle,
                  minHeight: entry.component.includes('generative_system') ? 220 : 90,
                  // Same disabled-input convention as AntwortKatalog's act-id field: opacity 0.6 +
                  // the native `disabled` attribute, nothing bespoke.
                  opacity: isFrozen ? 0.6 : 1 }}
                value={draft}
                disabled={isFrozen}
                onChange={e => setDraftValue(entry, e.target.value)}
              />

              {/* Pre-allocated action row — buttons swap in/out inside it but the row itself is
                  always present (same idiom as EinstellungenSeite's Speichern/Zurücksetzen rows).
                  A frozen entry renders none of the editable actions — nothing here is actionable
                  once BE-locked — but carries the one thing that IS relevant to it: the
                  Firmendaten link (see <constraints>). The row itself stays either way, keeping
                  every card's structure identical. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flexWrap: 'wrap', minHeight: 32 }}>
                {isFrozen && onNavigateToFirmendaten && (
                  <button type="button" onClick={onNavigateToFirmendaten} style={firmendatenLinkStyle()}>
                    Firmendaten
                  </button>
                )}
                {!isFrozen && (
                  <>
                    <button onClick={() => save(entry)} style={btnStyle('primary')}
                      disabled={savingComponent === entry.component || !dirty}>
                      {savingComponent === entry.component ? 'Speichern…' : 'Speichern'}
                    </button>

                    {dirty && (
                      <button onClick={() => setDraftValue(entry, entry.effectiveText)} style={btnStyle('ghost')}>
                        Zurücksetzen
                      </button>
                    )}

                    {entry.source === 'override' && (
                      resetConfirmComponent === entry.component ? (
                        <>
                          <span style={{ fontSize: 12, color: theme.colors.textSecondary }}>Auf Standard zurücksetzen?</span>
                          <button onClick={() => resetToStandard(entry.component)} style={btnStyle('danger')}
                            disabled={resettingComponent === entry.component}>
                            {resettingComponent === entry.component ? 'Setzt zurück…' : 'Bestätigen'}
                          </button>
                          <button onClick={() => setResetConfirmComponent(null)} style={btnStyle('ghost')}>
                            Abbrechen
                          </button>
                        </>
                      ) : (
                        <button onClick={() => setResetConfirmComponent(entry.component)} style={btnStyle('ghost')}>
                          Auf Standard zurücksetzen
                        </button>
                      )
                    )}

                    {canRollback && (
                      <button onClick={() => rollbackOne(entry)} style={btnStyle('ghost')}
                        disabled={rollingBackComponent === entry.component}>
                        {rollingBackComponent === entry.component ? 'Aktiviert…' : 'Vorherige Version aktivieren'}
                      </button>
                    )}

                    {savedComponent === entry.component && !dirty && (
                      <span style={{ fontSize: 12, color: '#1f7a3d' }}>gespeichert ✓</span>
                    )}
                  </>
                )}
              </div>

              {/* Reserved error zone — always rendered at full height (see InlineErrorZone) so the
                  bubble fading in/out inside it never shifts this card's own layout or spills into
                  the next card below. A frozen entry has no save/reset/rollback path to fail, so
                  this stays permanently empty there — same footprint as every other card. */}
              <InlineErrorZone rowKey={entry.component} message={fieldErrors[entry.component]} />
            </div>
          );
        })}
          </section>
        ))}

        {!loading && entries.length === 0 && (
          <div style={{ color: theme.colors.textSecondary, fontSize: 13 }}>Keine Antwort-Texte gefunden.</div>
        )}
      </div>
    </BentoCard>
  );
};

export default AntwortTexte;
